import type { FC, MouseEventHandler, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import FilterAltRoundedIcon from '@mui/icons-material/FilterAltRounded';
import type { BoxProps } from '@mui/material';
import {
  Box,
  Grid,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';

import useSurveyLogoAndName from 'src/hooks/useSurveyLogoAndName';
import LanguageSwitcher from 'src/components/common/LanguageSwitcher';
import SettingsMenu from 'src/components/common/SettingsMenu';
import SurveySwitcher from 'src/components/common/SurveySwitcher';
import BrandBox from 'src/components/app/Header/components/BrandBox';
import UserNavigation from 'src/components/app/Header/components/UserNavigation';

import DrawerNavigation from './components/DrawerNavigation';
import PreviousVersionBanner from './components/PreviousVersionBanner';
import TabNavigation from './components/TabNavigation';

import { styles } from './index.styles';

export interface HeaderProps extends BoxProps {
  onRightIconClick?: MouseEventHandler;
  guidedTourButton?: ReactNode;
  hideProjectLogo?: boolean;
  hideTabNavigation?: boolean;
  hidePreviousVersionBanner?: boolean;
  hideSurveySettings?: boolean;
}

const Header: FC<HeaderProps> = ({
  onRightIconClick,
  guidedTourButton = null,
  hideProjectLogo,
  hideTabNavigation,
  hidePreviousVersionBanner,
  hideSurveySettings,
}) => {
  const { t } = useTranslation('common');
  const theme = useTheme();
  const upLg = useMediaQuery(theme.breakpoints.up('lg'), { noSsr: true });
  const { surveyLogo, surveyName, showNameInHeader } = useSurveyLogoAndName();

  return (
    <>
      {upLg && !hidePreviousVersionBanner && <PreviousVersionBanner />}
      <Stack className="Header-root" sx={styles.header}>
        {upLg && (
          <Stack sx={styles.headerContainer}>
            <Grid container sx={styles.headerBanner}>
              <Grid
                item
                xs={hideProjectLogo ? 6 : 4}
                sx={{ pt: 1, display: 'flex', flexDirection: 'column' }}
              >
                <BrandBox />
              </Grid>

              {!hideProjectLogo && (
                <Grid
                  item
                  xs={4}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flexDirection: 'column',
                  }}
                >
                  <Box sx={styles.surveyLogo}>
                    {surveyLogo && (
                      <img alt={t('appLogo')} src={surveyLogo} height="100%" />
                    )}
                  </Box>
                  {showNameInHeader && (
                    <Box>
                      <Typography sx={{ fontWeight: 600 }}>
                        {surveyName}
                      </Typography>
                    </Box>
                  )}
                </Grid>
              )}

              <Grid item xs={hideProjectLogo ? 6 : 4} sx={{ pt: 1 }}>
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{ justifyContent: 'flex-end' }}
                >
                  <SurveySwitcher />
                  {!hideSurveySettings && <SettingsMenu />}
                  {guidedTourButton}
                  <LanguageSwitcher color="secondary" />
                </Stack>
              </Grid>
            </Grid>

            <Stack direction="row" sx={styles.headerNavigation} spacing={5}>
              <Stack direction="column" sx={{ justifyContent: 'flex-end' }}>
                {hideTabNavigation ? (
                  <Stack sx={{ width: '100%' }} />
                ) : (
                  <TabNavigation />
                )}
              </Stack>
              <UserNavigation />
            </Stack>
          </Stack>
        )}

        {!upLg && (
          <Grid className="Header-top" container sx={styles.headerTop}>
            <Grid item>
              <DrawerNavigation
                hideProjectLogo={hideProjectLogo}
                hideTabNavigation={hideTabNavigation}
                hidePreviousVersionBanner={hidePreviousVersionBanner}
                hideSurveySettings={hideSurveySettings}
              />
            </Grid>

            <Grid item>
              {!!onRightIconClick && (
                <IconButton onClick={onRightIconClick}>
                  <FilterAltRoundedIcon fontSize="large" />
                </IconButton>
              )}
            </Grid>
          </Grid>
        )}
      </Stack>
    </>
  );
};

export default Header;
